import { filterTypes } from "../constants";
import { isArray, map, set, flatten, isNil, snakeCase } from "lodash";
import { unassignedOption, FILTERS_BY_SPECIALTY, DEFAULT_FILTERS, FILTERS, NEW_PATIENT_STATUSES } from "../constants";
import { translate } from '@td/shared_utils';

const fromQueryParams = (queryParams) => {
  return Object.entries(queryParams).reduce((filters, [key, value]) => {
    if (isNil(value)) {
      return filters;
    }

    switch (key) {
      case filterTypes.PRACTICES:
      case filterTypes.PROVIDERS:
      case filterTypes.PROVIDER_LICENSES:
      case filterTypes.PAYMENT_METHODS:
      case filterTypes.STATUSES:
        filters[key] = isArray(value)
          ? value.map((value) => ({ value }))
          : [{ value }];
        break;
      case filterTypes.ACCEPT_NEW_PATIENTS_STATUS:
        queryParams[key] = NEW_PATIENT_STATUSES[value.value];
        break;

      default:
        filters[key] = value;
    }

    return filters;
  }, {});
};

const toQueryParams = (filters) => {
  return Object.entries(filters).reduce((queryParams, [key, value]) => {
    if (isNil(value)) {
      return queryParams;
    }

    switch (key) {
      case filterTypes.PRACTICES:
      case filterTypes.PROVIDERS:
      case filterTypes.PROVIDER_LICENSES:
      case filterTypes.PAYMENT_METHODS:
      case filterTypes.STATUSES:
        queryParams[key] = value.map(({ value }) => value);
        break;
      case filterTypes.ACCEPT_NEW_PATIENTS_STATUS:
        queryParams[key] = NEW_PATIENT_STATUSES[value.value];
        break;

      default:
        queryParams[key] = value;
    }

    return queryParams;
  }, {});
};

const toRequestParams = (filters, availableFilters) => {
  return Object.entries(filters).reduce((queryParams, [key, value]) => {
    if (isNil(value) || (isArray(value) && !value.length) || !availableFilters.includes(key))  {
      return queryParams;
    }

    switch (key) {
      case filterTypes.PRACTICES:
        set(queryParams, "practices.practiceTeamIds", map(value.filter(({ value }) => value !== unassignedOption.value), "value"));
        set(queryParams, "practices.unassigned", value.some(({ value }) => value === unassignedOption.value));
        break;
      case filterTypes.PROVIDERS:
        set(queryParams, "providerIds", value.map(({ value }) => value));
        break;
      case filterTypes.PROVIDER_LICENSES:
        set(queryParams, "states", value.map(({ value }) => value));
        break;
      case filterTypes.PAYMENT_METHODS:
      case filterTypes.STATUSES:
      case filterTypes.ACCEPT_NEW_PATIENTS_STATUS:
        queryParams[key] = value.map(({ value }) => value);
        break;
    }

    return queryParams;
  }, {});
};

export const fromViewFilterPreferences = (preferences) => {
  return Object.entries(preferences).reduce((filters, [key, value]) => {
    if (isNil(value)) {
      return filters;
    }

    switch (key) {
      case 'practices':
        const practices = [];

        if (value.unassigned) {
          practices.push(unassignedOption);
        }

        if (value.practiceTeamIds) {
          practices.push(...value.practiceTeamIds.map(value => ({ value })));
        }

        filters[filterTypes.PRACTICES] = practices;
        break;
      case 'providerIds':
        filters[filterTypes.PROVIDERS] = flatten([value]).map(value => ({ value }));
        break;
      case filterTypes.PAYMENT_METHODS:
      case filterTypes.PROVIDER_LICENSES:
      case filterTypes.STATUSES:
        filters[key] = flatten([value]).map(value => ({ value }));
        break;
      case filterTypes.ACCEPT_NEW_PATIENTS_STATUS:
        filters[key] = NEW_PATIENT_STATUSES[value.value];
        break;
    }

    return filters;
  }, {});
};

export const getAllowedFiltersBySpecialty = (specialtyCode) => {
  return FILTERS_BY_SPECIALTY[specialtyCode] || DEFAULT_FILTERS;
};

export const getFilterLabel = (name) => translate(null, `provider_panel.filters.fields.${snakeCase(name)}`, "label");
export const getFilterPlaceholder = (name) => translate(null, `provider_panel.filters.fields.${snakeCase(name)}`, "placeholder");

export const mapFiltersBySpecialty = ({ specialtyCode }) => {
  const availableFilters = getAllowedFiltersBySpecialty(specialtyCode).filter(
    (name) => window.FEATURE_TOGGLES.provider_panel_status_filter || name !== filterTypes.STATUSES
  )

  return availableFilters.map((filterType) => {
    const component = FILTERS[filterType];

    return {
      name: filterType,
      component,
    };
  });
};

export const dataMappers = {
  fromQueryParams,
  toQueryParams,
  toRequestParams,
  fromViewFilterPreferences,
  getAllowedFiltersBySpecialty,
  mapFiltersBySpecialty,
  getFilterLabel,
  getFilterPlaceholder,
};
